import React, { useCallback, useEffect, useState } from "react";
import baseUrl from "../../../../api/baseUrl";
import { inputKeyboardName } from "../../../../store/pos/posAction";
import {
  addItem,
  clearCustomer,
  resetPos,
  selectCustomer,
  setSaleType,
  setSaleDate,
  setCommonDiscountType,
  posChange,
} from "../../../../store/pos/posSlice";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import "../popups/customerlist/CustomerModel.scss";
import "./RecentBillModel.scss";
import { RootState } from "../../../../store/store";

function transformToNewData(existingItem: any, catalogItem?: any) {
  return {
    discount: Number(existingItem.lineDiscount || existingItem.discount || 0),
    discountHistoryId: existingItem.discountHistoryId,
    hasDiscount:
      Number(existingItem.lineDiscount || existingItem.discount || 0) > 0,
    itemId: existingItem.itemId,
    name:
      catalogItem?.name ||
      existingItem.Item?.name ||
      existingItem.itemName ||
      existingItem.name ||
      "Unknown Item",
    itemNumber:
      catalogItem?.itemNumber ||
      existingItem.Item?.itemNumber ||
      existingItem.itemNumber ||
      "",
    barcode:
      catalogItem?.barcode ||
      existingItem.Item?.barcode ||
      existingItem.barcode ||
      "",
    scale:
      catalogItem?.scale ||
      existingItem.Item?.scale ||
      existingItem.scale ||
      "Unit",
    image:
      catalogItem?.image ||
      existingItem.Item?.image ||
      existingItem.image ||
      "",
    stock:
      typeof catalogItem?.stock === "number"
        ? catalogItem.stock
        : Number(catalogItem?.stock || 0),
    modified:
      typeof catalogItem?.modified === "boolean" ? catalogItem.modified : true,
    price: Number(existingItem.price),
    priceHistoryId: existingItem.priceHistoryId,
    quantity: Number(existingItem.quantity),
    tax: Number(existingItem?.tax || 0),
    taxHistoryId: existingItem.taxHistoryId,
  };
}

const RecentBillModel = ({
  isCloseMain,
  isOpen,
  onEditBill,
}: {
  isCloseMain: () => void;
  isOpen: boolean;
  onEditBill?: (billNumber: string | null) => void;
}) => {
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [bills, setBills] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();
  const allItems = useSelector((state: RootState) => state.pos.allItems || []);

  const formatMoney = (value: any) => {
    const numeric = Number(value || 0);
    return Number.isNaN(numeric) ? "0.00" : numeric.toFixed(2);
  };

  const toNumberValue = (value: any) => {
    const numeric = Number(value || 0);
    return Number.isNaN(numeric) ? 0 : numeric;
  };

  const formatDateTime = (value: any) => {
    if (!value) {
      return "-";
    }
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) {
      return "-";
    }
    return dt.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      dispatch(inputKeyboardName({ name: "" }));
      isCloseMain();
    }
  };

  // Fetch bills only once on component mount
  useEffect(() => {
    const fetchBills = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const { data } = await baseUrl.get("branch/pos/get-total-sales-last");
        setBills(data.data || []);
      } catch (error) {
        setErrorMessage(
          "Unable to load recent bills. Please check POS API route.",
        );
        console.error("Error fetching bills:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBills();
  }, []);

  const handleEditBill = useCallback(
    async (bill: any) => {
      setSelectedCustomer(bill);

      try {
        const { data } = await baseUrl.get(
          `branch/pos/get-sales-by-bill/${bill.id}`,
        );
        const fetchedSalesData = data.data || [];

        if (!fetchedSalesData.length) {
          return;
        }

        // Clear current POS draft before loading selected bill for editing.
        dispatch(resetPos());

        if (bill.customerId) {
          dispatch(
            selectCustomer({
              id: bill.customerId,
              name:
                bill.Customer?.name || bill.customerName || "Unknown Customer",
              phoneNumber:
                bill.Customer?.phoneNumber || bill.customerPhone || "",
              customerType:
                bill.Customer?.customerType || bill.customerType || "normal",
            }),
          );
        } else {
          dispatch(clearCustomer());
        }

        // Restore sale type if API includes it; otherwise default to cash.
        dispatch(setSaleType(bill.creditSale ? "credit" : "cash"));

        const newItems = fetchedSalesData.map((saleRow: any) => {
          const catalogItem = allItems.find(
            (catalog: any) => String(catalog.id) === String(saleRow.itemId),
          );
          return transformToNewData(saleRow, catalogItem);
        });
        newItems.forEach((item: any) => {
          dispatch(addItem(item));
        });

        dispatch(
          posChange({
            name: "billNumber",
            value: bill.billNumber || "",
          }),
        );
        dispatch(posChange({ name: "cash", value: toNumberValue(bill.cash) }));
        dispatch(posChange({ name: "card", value: toNumberValue(bill.card) }));
        dispatch(posChange({ name: "bank", value: toNumberValue(bill.bank) }));
        dispatch(setCommonDiscountType("amount"));
        dispatch(
          posChange({
            name: "commonDiscount",
            value: toNumberValue(bill.givenDiscount || bill.discount),
          }),
        );

        dispatch(
          setSaleDate(
            bill.saleEntryTime ||
              bill.createdAt ||
              fetchedSalesData[0]?.createdAt ||
              null,
          ),
        );

        onEditBill?.(bill.billNumber || null);

        isCloseMain();
      } catch (error) {
        console.error("Error fetching sales data:", error);
      }
    },
    [dispatch, isCloseMain, onEditBill, allItems],
  );

  if (!isOpen) return null;

  return (
    <div
      className="pos_modal_overlay recent_bills_overlay"
      onClick={handleOverlayClick}
    >
      <div className="pos_modal_container customer_model recent_bills_model">
        <div className="pos_modal_header recent_bills_header">
          <h2 className="pos_modal_title">Recent Bills</h2>
          <button
            type="button"
            className="pos_modal_close"
            onClick={() => {
              dispatch(inputKeyboardName({ name: "" }));
              isCloseMain();
            }}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="pos_modal_body scrollable recent_bills_content">
          <div className="recent_bills_stats">
            <div className="stat_item">
              <span className="stat_label">Total Bills</span>
              <span className="stat_value">{bills.length}</span>
            </div>
          </div>

          {errorMessage && (
            <div className="recent_bills_error">{errorMessage}</div>
          )}

          <div className="customers recent_bills_list">
            <div className="recent_bills_header_row">
              <span className="col_bill">Bill No</span>
              <span className="col_amount">Amount</span>
              <span className="col_net">Net Total</span>
              <span className="col_customer">Customer</span>
              <span className="col_date">Date</span>
              <span className="col_action">Action</span>
            </div>

            {isLoading ? (
              <div className="recent_bills_empty">Loading recent bills...</div>
            ) : bills?.length ? (
              bills.map((bill: any) => (
                <div
                  key={bill.id}
                  className={`customer_item recent_bill_row ${
                    selectedCustomer?.id === bill.id ? "selected" : ""
                  }`}
                  onClick={() => setSelectedCustomer(bill)}
                >
                  <span
                    className="customer_name bill_number cell_bill"
                    data-label="Bill No"
                  >
                    {bill.billNumber || "-"}
                  </span>
                  <span
                    className="customer_name cell_amount"
                    data-label="Amount"
                  >
                    {formatMoney(bill.amountRecived)}
                  </span>
                  <span
                    className="customer_name cell_net"
                    data-label="Net Total"
                  >
                    {formatMoney(bill.netTotal)}
                  </span>
                  <span
                    className="customer_name cell_customer"
                    data-label="Customer"
                  >
                    {bill.customerId
                      ? bill.Customer?.name || bill.customerName || "Unknown"
                      : "Walk-in"}
                  </span>
                  <span className="customer_name cell_date" data-label="Date">
                    {formatDateTime(bill.createdAt)}
                  </span>
                  <button
                    className="select_btn cell_action"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditBill(bill);
                    }}
                  >
                    Edit Bill
                  </button>
                </div>
              ))
            ) : (
              <div className="recent_bills_empty">
                No recent bills available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentBillModel;
